import { useEffect, useMemo, useState } from "react";
import { Column } from "primereact/column";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Message } from "primereact/message";
import toast from "react-hot-toast";

import { EmptyState, PageHeader, StatusBadge } from "../../components/ui";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useTenantScope } from "../../hooks/useTenantScope";
import { useTenantOptions } from "../../hooks/useTenantOptions";
import {
  POINT_REDEMPTION_CANCELLATION_LABELS,
  POINT_REDEMPTION_STATUS_LABELS,
  POINT_REDEMPTION_STATUS_TONES,
  PointRedemption,
  PointRedemptionFilters as PointRedemptionQueryFilters,
  formatPointRedemptionTimeLeft,
  resolvePointRedemptionStatus,
} from "../../models/pointRedemption";
import { formatPointRewardCost } from "../../models/pointReward";
import {
  useExportPointRedemptionsMutation,
  useGetPointRedemptionsQuery,
} from "../../store/services/point-redemptions";
import { useGetPointRewardsQuery } from "../../store/services/point-rewards";
import { toReportDateParam } from "../../utils/constants/report-date-range";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { buildAnalyticsExportFilename, downloadBlob } from "../../utils/functions/downloadBlob";
import { formatDateWithoutSeconds } from "../../utils/functions/date";
import {
  BenefitsMetricsRefreshingIndicator,
  BenefitsMetricsTenantScopeHeader,
} from "../Reporting/components/BenefitsMetricsWidgets";
import { PointsPageCard, PointsPageLoading } from "../PointRules/components/PointsPageCard";
import {
  EMPTY_POINT_REDEMPTION_FILTERS,
  PointRedemptionFilterValues,
  PointRedemptionFilters,
  hasPointRedemptionFilters,
} from "./components/PointRedemptionFilters";

const PAGE_TITLE = "Canjes";
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const DEFAULT_ROWS = 25;
const NOW_REFRESH_MS = 60_000;
const EMPTY_EXPORT_MESSAGE = "No hay canjes para los filtros seleccionados";
const TRUNCATED_EXPORT_WARNING =
  "El CSV llegó al tope de 20.000 filas y quedó recortado. Achicá el rango de fechas para exportar el resto.";

const toDayKeyOrNull = (date: Date | null): string | null => (date ? toReportDateParam(date) : null);

/** Fecha que completa el estado: cuándo vence, venció, se entregó o se canceló. */
const describeRedemptionDate = (redemption: PointRedemption, nowMs: number): string => {
  const status = resolvePointRedemptionStatus(redemption);
  if (status === "pending") {
    return formatPointRedemptionTimeLeft(redemption.qrExpiresAt, nowMs) ?? "Vencido";
  }
  if (status === "expired") return `Venció el ${formatDateWithoutSeconds(redemption.qrExpiresAt)}`;
  if (status === "fulfilled") {
    return redemption.fulfilledAt ? `Entregado el ${formatDateWithoutSeconds(redemption.fulfilledAt)}` : "—";
  }
  return redemption.cancelledAt ? `Cancelado el ${formatDateWithoutSeconds(redemption.cancelledAt)}` : "—";
};

export const PointRedemptions = () => {
  const scope = useTenantScope();
  const pointsFlag = useIsFeatureFlagEnabled("gamification_points_enabled");
  const { options: tenantOptions } = useTenantOptions();
  // SUPER_ADMIN en "Todos" ve los canjes de todas las instituciones: ahí no aplica el flag de un tenant.
  const isAllTenantsScope = !scope.loading && scope.role === "SUPER_ADMIN" && scope.tenantId === null;
  const flagReady = isAllTenantsScope || (!pointsFlag.loading && pointsFlag.enabled);
  const tenantId = scope.tenantId;

  const [filters, setFilters] = useState<PointRedemptionFilterValues>(EMPTY_POINT_REDEMPTION_FILTERS);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [filtersTenantId, setFiltersTenantId] = useState(tenantId);

  // Cambiar el tenant del header vuelve a la primera página y limpia el premio, que era de otro catálogo.
  if (filtersTenantId !== tenantId) {
    setFiltersTenantId(tenantId);
    setFilters((current) => ({ ...current, rewardId: null }));
    setPage(1);
  }

  // El tiempo restante se cuenta desde qr_expires_at; se refresca cada minuto mientras la tabla está abierta.
  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), NOW_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, []);

  const queryFilters: PointRedemptionQueryFilters = {
    tenantId,
    status: filters.status,
    from: toDayKeyOrNull(filters.dateFrom),
    to: toDayKeyOrNull(filters.dateTo),
    rewardId: filters.rewardId,
  };

  const {
    currentData: redemptionPage,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetPointRedemptionsQuery(
    { ...queryFilters, page, limit: rows },
    { skip: scope.loading || !flagReady }
  );
  const { currentData: rewards } = useGetPointRewardsQuery(tenantId ?? "", {
    skip: tenantId === null || !flagReady,
  });
  const [exportPointRedemptions, exportState] = useExportPointRedemptionsMutation();

  const rewardOptions = useMemo(
    () => (rewards ?? []).map((reward) => ({ label: reward.title, value: reward.id })),
    [rewards]
  );
  const tenantName = (id: string): string =>
    tenantOptions.find((option) => option.value === id)?.label ?? id;

  const changeFilters = (next: PointRedemptionFilterValues) => {
    setFilters(next);
    setPage(1);
  };

  const handlePage = (event: DataTablePageEvent) => {
    setRows(event.rows);
    setPage(Math.floor(event.first / event.rows) + 1);
  };

  const handleExport = async () => {
    if (redemptionPage?.total === 0) {
      toast(EMPTY_EXPORT_MESSAGE);
      return;
    }
    try {
      const result = await exportPointRedemptions(queryFilters).unwrap();
      const filename = `canjes-puntos_${tenantId ?? "todos-los-tenants"}_${queryFilters.from ?? "inicio"}_${queryFilters.to ?? "fin"}.csv`;
      downloadBlob(result.blob, buildAnalyticsExportFilename(filename, tenantOptions));
      if (result.truncated) {
        toast(TRUNCATED_EXPORT_WARNING, { icon: "⚠️" });
      } else {
        toast.success("CSV exportado");
      }
    } catch (exportError: unknown) {
      toast.error(getApiErrorMessage(exportError) ?? "No se pudo exportar el CSV");
    } finally {
      // Sin esto el blob queda retenido en el store de RTK hasta desmontar la pantalla.
      exportState.reset();
    }
  };

  if (scope.loading) {
    return <PointsPageLoading title={PAGE_TITLE} />;
  }

  if (!isAllTenantsScope) {
    if (pointsFlag.loading) {
      return <PointsPageLoading title={PAGE_TITLE} />;
    }
    if (!pointsFlag.enabled) {
      return (
        <PointsPageCard>
          <PageHeader title={PAGE_TITLE} />
          <div className="flex justify-content-center p-4">
            <Message severity="info" text="La sección está deshabilitada para este tenant." />
          </div>
        </PointsPageCard>
      );
    }
  }

  const userBodyTemplate = (redemption: PointRedemption) => (
    <div className="flex flex-column">
      <span>{redemption.user.fullName || "—"}</span>
      <small className="text-600">{redemption.user.email}</small>
    </div>
  );

  const rewardBodyTemplate = (redemption: PointRedemption) => (
    <div className="flex flex-column">
      <span>{redemption.reward.title}</span>
      {redemption.reward.partnerName && (
        <small className="text-600">{redemption.reward.partnerName}</small>
      )}
    </div>
  );

  const statusBodyTemplate = (redemption: PointRedemption) => {
    const status = resolvePointRedemptionStatus(redemption);
    return (
      <div className="flex flex-column align-items-start" style={{ gap: "var(--space-6)" }}>
        <StatusBadge
          label={POINT_REDEMPTION_STATUS_LABELS[status]}
          tone={POINT_REDEMPTION_STATUS_TONES[status]}
        />
        {status === "cancelled" && redemption.cancellationReason && (
          <small className="text-600">
            {POINT_REDEMPTION_CANCELLATION_LABELS[redemption.cancellationReason]}
          </small>
        )}
      </div>
    );
  };

  return (
    <PointsPageCard>
      <BenefitsMetricsTenantScopeHeader tenantId={tenantId} />
      <PageHeader title={PAGE_TITLE} />

      <PointRedemptionFilters
        filters={filters}
        onChange={changeFilters}
        rewardOptions={rewardOptions}
        rewardFilterDisabled={tenantId === null}
        onExport={() => void handleExport()}
        isExporting={exportState.isLoading}
      />
      <BenefitsMetricsRefreshingIndicator visible={isFetching && !isLoading} />

      {isError ? (
        <Message severity="error" text={getApiErrorMessage(error) ?? "No se pudieron cargar los canjes."} />
      ) : (
        <DataTable
          value={redemptionPage?.items ?? []}
          loading={isLoading}
          dataKey="id"
          lazy
          paginator
          rows={rows}
          first={(page - 1) * rows}
          totalRecords={redemptionPage?.total ?? 0}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPage={handlePage}
          // "Vencimiento / entrega" depende de nowMs, que no es parte de la fila: con el memo
          // por defecto (compara solo rowData) el tiempo restante quedaba congelado.
          cellMemo={false}
          stripedRows
          responsiveLayout="scroll"
          emptyMessage={<EmptyState entity="canjes" filtered={hasPointRedemptionFilters(filters)} />}
        >
          {isAllTenantsScope && (
            <Column header="Tenant" body={(redemption: PointRedemption) => tenantName(redemption.tenantId)} />
          )}
          <Column header="Usuario" body={userBodyTemplate} />
          <Column header="Premio" body={rewardBodyTemplate} />
          <Column
            header="Puntos"
            body={(redemption: PointRedemption) => formatPointRewardCost(redemption.pointsSpent)}
          />
          <Column header="Estado" body={statusBodyTemplate} />
          <Column
            header="Código"
            body={(redemption: PointRedemption) => (
              <span style={{ fontFamily: "monospace", letterSpacing: "0.08em" }}>
                {redemption.qrShortCode}
              </span>
            )}
          />
          <Column
            header="Fecha de canje"
            body={(redemption: PointRedemption) => formatDateWithoutSeconds(redemption.createdAt)}
          />
          <Column
            header="Vencimiento / entrega"
            body={(redemption: PointRedemption) => describeRedemptionDate(redemption, nowMs)}
          />
        </DataTable>
      )}
    </PointsPageCard>
  );
};
